<template>
  <div id="app" v-cloak>
    <CoinDisplay v-if="showCoinDisplay" ref="coinDisplay" :initial-amount="userInfo.nowMoney" />
    <RedPacketNotification :visible.sync="showRedPacket" :auto-close="3000" :text="redPacketText" />
    <RechargePopup :show.sync="showRecharge"/>
    <keep-alive>
      <router-view v-if="isAppInitialized && $route.meta.keepAlive" />
    </keep-alive>
    <router-view v-if="isAppInitialized && !$route.meta.keepAlive" />
  </div>
</template>

<script>
import { parseQuery } from "@utils";
import { _code } from "@utils/constant";
import CoinDisplay from "@/views/home/model/CoinDisplay";
import RedPacketNotification from '@/views/home/model/RedPacketNotification.vue';
import {initWebsocket, setMessageCallback, webSocketClose} from '@/utils/webSocketManager';
import RechargePopup from "@views/home/model/popup/RechargePopup.vue";

export default {
  name: 'App',
  data() {
    return {
      isAppInitialized: false,
      showRedPacket: false,
      showCoinDisplay: false,
      showRecharge: false,
      redPacketText: '获得红包奖励',
      _visibilityHandler: null,
    }
  },
  computed: {
    userInfo() {
      return this.$store.getters.userInfo;
    }
  },
  components: {RechargePopup, CoinDisplay, RedPacketNotification },
  provide() {
    return { app: this }
  },
  async created() {
    await this.initializeApp();
  },
  mounted() {
    this.$Bus.$on("triggerOpenRecharge", () => {
      this.showRecharge = true;
    });
  },
  destroyed() {
    this.$Bus.$off("triggerOpenRecharge");
    this.cleanup();
  },
  methods: {
    async initializeApp() {
      try {
        const liveId = this.$route.query.liveId || parseQuery()["liveId"];
        const uid = this.$route.query.uid || parseQuery()["uid"];
        // const liveId = '1f5ba519cea195b8edddffaae14eedce';
        // const uid = '679fc99b5f87805f2a7fd8109b07372d';

        if (!uid || !liveId){
          this.$toast.fail({message:'缺少必要的初始化参数 uid 或 liveId'});
          window.finishLoading && window.finishLoading();  // 异常也要关，不能卡死
          return;
        }

        this.setupVisibilityListener();

        this.$store.commit("SET_UID", uid);
        this.$store.commit("live/setLiveId", liveId);

        await this.$store.dispatch("login", uid);

        await Promise.all([
          this.$store.dispatch("USERINFO"),
          this.$store.dispatch("getAppConfig"),
          this.$store.dispatch("live/getLiveInfo", liveId),
        ]);

        this.isAppInitialized = true;
        this.setupWebSocket(liveId);

        // 🆕 初始化全部完成后再关 Loading
        window.finishLoading && window.finishLoading();
      } catch (error) {
        let errMsg = '初始化失败，请刷新页面重试';
        if (error.message.includes('缺少必要')) {
          errMsg = error.message;
        } else if (error.response && error.response.data) {
          errMsg = error.response.data.msg || '网络请求异常';
        }
        this.$toast.fail(errMsg);
        // 🆕 失败也要关 Loading
        window.finishLoading && window.finishLoading();
      }
    },

    setupVisibilityListener() {
      this._visibilityHandler = async () => {
        if (document.visibilityState === 'visible') {
          console.log('📱 页面可见，尝试恢复接口和WebSocket连接');
          const uid = this.$store.state.app.uid; // 请确认路径是否为 this.$store.state.app.uid
          const liveId = this.$store.state.live.id;
          if (!uid || !liveId) return;

          try {
            // 1. 重新登录刷新 Token
            await this.$store.dispatch("login", uid);
            console.log('✅ 唤醒后重新登录成功，Token已刷新');

            // 2. 【关键修改】先同步最新直播状态，再连 WS，防止 WS 推来消息时本地状态是旧的
            await this.$store.dispatch("live/getLiveInfo", liveId);

            // 3. 干掉旧的死连接
            webSocketClose(true);

            // 4. 建立新连接（此时 Vuex 里的 Token 和 LiveInfo 都是最新的）
            initWebsocket(liveId);

            // 5. 判断游戏或抽奖状态，如果正在进行，跳到主页
            const liveState = this.$store.state.live;
            if (liveState.isGamePlaying || liveState.isLotteryPlaying) {
              this.$toast("准备！");
              if(liveState.isGamePlaying){
                // 【修改】commit 是同步操作，不需要 await
                this.$store.commit("live/openGameReady",liveState.playingGameId);
              }
              if(liveState.isLotteryPlaying){
                this.$store.commit("live/openLotteryReady",liveState.drawingId);
              }
              await this.$router.replace({path: "/"});
            }
          } catch (error) {
            console.error('❌ 唤醒后重新登录失败:', error);
          }
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    },

    setupWebSocket(liveId) {
      if (!liveId) return;
      initWebsocket(liveId);
      setMessageCallback(this.handleSocketMessage);
    },

    handleSocketMessage(msg) {
      if (msg.msgType === _code.HEAT_BEAT) return;
      if (!msg.liveId || msg.liveId !== this.$store.state.live.id) return;
      this.$store.dispatch('broadcastMessage', msg);
      this.processSpecificMessage(msg);
    },

    processSpecificMessage: function (msg) {
      switch (msg.msgType) {
        case _code.CONN_SUCCESS:
          this.$toast("iChat连接成功！");
          break;
        case _code.INIT_LOTTERY:
        case _code.CLEAR_LIVE:
        case _code.READY_GAME:
          this.$router.replace({path: "/"});
          break;
        case _code.START_GAME:
          this.$toast("游戏开始！");
          this.$store.commit("liveGame/upStatus", 1);
          this.showCoinDisplay = true;
          break;
        case _code.OPEN_WISH_WALL: // 打开祝福榜
          this.$Bus.$emit("openLuckyWheel", msg);
          break;
        case _code.SIGN_REWARD_SUCCESS:
        case _code.GET_RED_PACKET_SUCCESS: {
          const jsonData = JSON.parse(msg.data);
          this.showRedPacket = true;
          this.redPacketText = "获得红包奖励¥" + jsonData.quantity;
          break;
        }
        case _code.COIN_REWARD: {
          //更新用户金币
          this.$store.dispatch("USERINFO");
          break;
        }
        case _code.END_GAME:
          const jsonData = JSON.parse(msg.data);
          this.showCoinDisplay = false;
          console.log('liveGameId:', jsonData.id);
          this.$store.commit("liveGame/setLiveGameId", jsonData.id);
          this.$router.replace("/game/gameRank");
          break;
      }
    },

    cleanup() {
      if (this._visibilityHandler) {
        document.removeEventListener('visibilitychange', this._visibilityHandler);
      }
      webSocketClose(true);
      this.$store.commit('live/closeGameReady');
      this.$store.commit('live/closeLottery');
      this.$store.commit('LOGOUT');
    }
  }
}
</script>

<style lang="less">
/* 样式部分保持不变 */
[v-cloak] {
  display: none !important;
}
.router-view {
  position: absolute;
  width: 100%;
}
.fold-left-enter-active {
  animation: fold-left-in .5s;
}
.fold-left-leave-active {
  animation: fold-left-out .5s;
}
@keyframes fold-left-in {
  0% { transform: translate3d(100%, 0, 0); }
  100% { transform: translate3d(0, 0, 0); }
}
@keyframes fold-left-out {
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(-100%, 0, 0); }
}
.fold-right-enter-active {
  animation: fold-right-in .5s;
}
.fold-right-leave-active {
  animation: fold-right-out .5s;
}
@keyframes fold-right-in {
  0% { transform: translate3d(-100%, 0, 0); }
  100% { transform: translate3d(0, 0, 0); }
}
@keyframes fold-right-out {
  0% { transform: translate3d(0, 0, 0); }
  100% { transform: translate3d(100%, 0, 0); }
}
</style>
